import { useEffect as useDrawerBaselineEffect, useEffectEvent as useDrawerBaselineEvent } from "react";
import { useDirtySnapshot } from "../../../../../../../shared/frontend/forms/useDirtySnapshot";
import { formSnapshot } from "../../../../../../../shared/frontend/forms/formSnapshot";
import type { Dayjs } from "dayjs";
import type { UnitsSettings } from "../../../app/units/units";
import { App } from "../../../../../../../shared/frontend/ui";
import { Form, Select } from "../../../../../../../shared/frontend/ui";
import { DatePicker } from "../../../../../../../shared/frontend/components/ControlledPickers";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import dayjs from "dayjs";
import React from "react";
import { useAuth } from "../../../app/auth/useAuth";
import { requireAccountAbbreviationConfirmation } from "../../../app/utils/accountAbbreviationConfirmation";
import { accountName } from "../../../app/utils/accountName";
import { INTERNAL_FORM_AUTOCOMPLETE } from "../../../app/utils/browserAutofill";
import { contractDraftRequiresSepa,prepareContractServices,prepareContractWallets } from "../../../app/utils/contractCreation";
import { defaultBankAccountHolder,normalizeBic,normalizeIban } from "../../../app/utils/sepa";
import type { ContractOptionGroupDto, OptionGroupSelectionState } from "../../../app/utils/contractOptionGroups";
import { toOptionGroupPayload, validateOptionGroupSelection } from "../../../app/utils/contractOptionGroups";
import { useFeedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { FormDrawer } from "../../../components/FormDrawer";
import { FormSectionHeading } from "../../../components/FormSectionHeading";
import { SepaBankDetailsFields } from "../../../components/SepaBankDetailsFields";
import { ResponsiveFormGrid } from "../../../components/ui/antd";
import type { MemberPromotionTemplateDto,PersonOptionDto } from "../../memberPromotions/memberPromotionApi";
import { memberPromotionApi,personOptionLabel } from "../../memberPromotions/memberPromotionApi";
import type { PersonDto } from "../types";
import { PersonContractCreateContentsSection } from "./PersonContractCreateContentsSection";
import type {
  ContractCreateFormValues,
  ContractServiceDraft,
  ContractTemplateComponentDto,
  ContractTemplateDto,
  ContractWalletDraft,
  WalletProductDto
} from "./personDetailTypes";
import { formatDateOnlyIso } from "./personDetailUtils";
import type { TimeUnitValue } from "../../../app/constants/timeUnit";

type SelectOption = {
  value: string | number;
  label: React.ReactNode;
};

type StudioOption = {
  id: string;
  name: string;
};

type AccountOption = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  name: string;
  email?: string | null;
};

type InstallmentPlanMeta = {
  planId: string;
  planName: string;
  min: number;
  max: number;
};

type InstallmentPreview = {
  count: number;
  installment: number;
  total: number;
  cycle: string;
};

type PersonContractCreateDrawerProps = {
  open: boolean;
  saving: boolean;
  setSaving: (saving: boolean) => void;
  form: FormInstance<ContractCreateFormValues>;
  person: PersonDto | null;
  personId: string;
  allowedStudios: StudioOption[];
  accounts: AccountOption[];
  closingAccountId: string;
  setClosingAccountId: (accountId: string) => void;
  contractVariantOptions: Array<{ value: string; label: string }>;
  selectedVariant: ContractTemplateDto | null;
  selectedVariantMeta: { allowOverridesOnCreate: boolean } | null;
  bundleServiceDraft: ContractServiceDraft[];
  setBundleServiceDraft: React.Dispatch<React.SetStateAction<ContractServiceDraft[]>>;
  bundleWalletDraft: ContractWalletDraft[];
  contractOptionGroups: ContractOptionGroupDto[];
  optionGroupSelection: OptionGroupSelectionState;
  setOptionGroupSelection: (next: OptionGroupSelectionState) => void;
  contractTemplateDefaultPriceOf: (contractTemplateId: string) => number | null | undefined;
  watchedContractStartDate?: Dayjs | null;
  watchedTrainingStartDate?: Dayjs | null;
  primaryService: ContractServiceDraft | ContractTemplateComponentDto | null;
  installmentPlanMeta: InstallmentPlanMeta | null;
  selectedPaymentPlan: unknown | null;
  installmentPreview: InstallmentPreview | null;
  walletProducts: WalletProductDto[];
  paymentMethodSelectOptions: SelectOption[];
  canUseMemberPromotions: boolean;
  dateInputFormat: string | string[];
  decimalSeparator: string;
  currencyCode: string;
  moneyPlaceholder: string;
  units: UnitsSettings;
  api: {
    get: <T = unknown>(url: string) => Promise<T>;
    post: <T = unknown>(url: string, body?: unknown) => Promise<T>;
    put: <T = unknown>(url: string, body?: unknown) => Promise<T>;
  };
  modal: {
    confirm: ReturnType<typeof App.useApp>["modal"]["confirm"];
  };
  text: (key: string) => string;
  billingCycleLabel: (value?: number | null, unit?: number | null) => string;
  cycleUnitLabel: (unit?: number | null) => string;
  usageLimitUnitLabel: (unit?: TimeUnitValue | null) => string;
  timeUnitLabel: (unit?: number | null) => string;
  accessScopeLabel: (scope?: number | null) => string;
  canChangeHomeStudio: boolean;
  onCancel: () => void;
  onValuesChange?: () => void;
  onChangeHomeStudio: (homeStudioId: string) => Promise<void>;
  onCreated: () => Promise<void> | void;
};

const CONTRACT_CREATE_FORM_ID = "person-contract-create-form";

function safeDraftText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function buildPersonBankUpdateDto(person: PersonDto, values: ContractCreateFormValues) {
  return {
    firstName: person.firstName,
    lastName: person.lastName,
    title: person.title ?? null,
    useDu: person.useDu ?? null,
    gender: person.gender ?? null,
    dateOfBirth: person.dateOfBirth ?? null,
    email: person.email ?? null,
    phone: person.phone ?? null,
    mobile: person.mobile ?? null,
    street: person.street ?? null,
    zip: person.zip ?? null,
    city: person.city ?? null,
    country: person.country ?? null,
    bankAccountHolder: values.bankAccountHolder ? String(values.bankAccountHolder).trim() : null,
    iban: values.iban ? normalizeIban(values.iban) : null,
    bic: values.bic ? normalizeBic(values.bic) : null,
    bankName: values.bankName ? String(values.bankName).trim() : null,
    mandateReference: values.mandateReference ? String(values.mandateReference).trim() : null,
    mandateSignedDate: formatDateOnlyIso(values.mandateSignedDate),
    homeStudioId: person.homeStudioId ?? null,
    doNotContactMessages: person.doNotContactMessages ?? false,
    doNotContactCalls: person.doNotContactCalls ?? false
  };
}

export function PersonContractCreateDrawer({
  open,
  saving,
  setSaving,
  form,
  person,
  personId,
  allowedStudios,
  accounts,
  closingAccountId,
  setClosingAccountId,
  contractVariantOptions,
  selectedVariant,
  selectedVariantMeta,
  bundleServiceDraft,
  setBundleServiceDraft,
  bundleWalletDraft,
  contractOptionGroups,
  optionGroupSelection,
  setOptionGroupSelection,
  contractTemplateDefaultPriceOf,
  watchedContractStartDate,
  watchedTrainingStartDate,
  primaryService,
  installmentPlanMeta,
  selectedPaymentPlan,
  installmentPreview,
  walletProducts,
  paymentMethodSelectOptions,
  canUseMemberPromotions,
  dateInputFormat,
  decimalSeparator,
  currencyCode,
  moneyPlaceholder,
  units,
  api,
  modal,
  text,
  billingCycleLabel,
  cycleUnitLabel,
  usageLimitUnitLabel,
  timeUnitLabel,
  accessScopeLabel,
  canChangeHomeStudio,
  onCancel,
  onValuesChange,
  onChangeHomeStudio,
  onCreated
}: PersonContractCreateDrawerProps) {
  const createContractFields = Form.useWatch(formSnapshot, { form: form, preserve: true });
  const { dirty: createContractDirty, markSaved: createContractMarkSaved } = useDirtySnapshot({ fields: createContractFields, extra: { closingAccountId, bundleServiceDraft, bundleWalletDraft, optionGroupSelection } });
  const createContractInitializeDirty = useDrawerBaselineEvent(() => createContractMarkSaved({ fields: formSnapshot(form.getFieldsValue(true)), extra: { closingAccountId, bundleServiceDraft, bundleWalletDraft, optionGroupSelection } }));

  const feedback = useFeedback();
  const auth = useAuth();
  const { modal: appModal } = App.useApp();
  const isCollectionUser = auth.info?.isCollectionUser === true;
  const [promotionTemplates, setPromotionTemplates] = React.useState<MemberPromotionTemplateDto[]>([]);
  const [referrerOptions, setReferrerOptions] = React.useState<PersonOptionDto[]>([]);
  const lastAutoMandateSignedDateIsoRef = React.useRef<string | null>(null);
  const watchedServicePaymentMethod = Form.useWatch("servicePaymentMethod", form);
  const watchedPackagePaymentMethod = Form.useWatch("paymentMethod", form);
  const watchedClosingDate = Form.useWatch("closingDate", form);
  const requiresSepaBankDetails = React.useMemo(
    () => contractDraftRequiresSepa(
      (bundleServiceDraft || []).map((service) => ({
        ...service,
        contractTemplateId: String(service.contractTemplateId || service.serviceProductId || ""),
        contractTemplateName: safeDraftText(service.serviceProductName)
      })),
      watchedServicePaymentMethod,
      watchedPackagePaymentMethod != null ? Number(watchedPackagePaymentMethod) : null
    ),
    [bundleServiceDraft, watchedPackagePaymentMethod, watchedServicePaymentMethod]
  );

  React.useEffect(() => {
    if (!open || !person) return;
    lastAutoMandateSignedDateIsoRef.current = null;
    form.setFieldsValue({
      bankAccountHolder: String(person.bankAccountHolder || "").trim() || defaultBankAccountHolder(person.firstName, person.lastName) || null,
      iban: person.iban ?? null,
      bic: person.bic ?? null,
      bankName: person.bankName ?? null,
      mandateReference: person.mandateReference ?? null,
      mandateSignedDate: person.mandateSignedDate ? dayjs(person.mandateSignedDate) : null
    });
  }, [form, open, person]);

  React.useEffect(() => {
    if (!open || !requiresSepaBankDetails) return;
    if (person?.mandateSignedDate) return;
    const closingDateIso = formatDateOnlyIso(watchedClosingDate);
    if (!closingDateIso) return;
    const currentIso = formatDateOnlyIso(form.getFieldValue("mandateSignedDate"));
    if (form.isFieldTouched("mandateSignedDate") && currentIso !== lastAutoMandateSignedDateIsoRef.current) return;
    if (currentIso && currentIso !== lastAutoMandateSignedDateIsoRef.current) return;
    if (currentIso === closingDateIso) return;
    form.setFieldValue("mandateSignedDate", dayjs(watchedClosingDate));
    lastAutoMandateSignedDateIsoRef.current = closingDateIso;
  }, [form, open, person?.mandateSignedDate, requiresSepaBankDetails, watchedClosingDate]);

  React.useEffect(() => {
    if (!open || !canUseMemberPromotions) {
      setPromotionTemplates([]);
      setReferrerOptions([]);
      return;
    }
    memberPromotionApi.listTemplates(false).then(setPromotionTemplates).catch(() => setPromotionTemplates([]));
    memberPromotionApi.searchPersons("").then((rows) => {
      setReferrerOptions(rows.filter((row) => row.id !== personId));
    }).catch(() => setReferrerOptions([]));
  }, [canUseMemberPromotions, open, personId]);

  const searchReferrers = React.useCallback((q: string) => {
    if (!canUseMemberPromotions) return;
    memberPromotionApi.searchPersons(q).then((rows) => {
      setReferrerOptions(rows.filter((row) => row.id !== personId));
    }).catch(() => setReferrerOptions([]));
  }, [canUseMemberPromotions, personId]);

  const handleSave = async () => {
    // Pflichtfelder prüft das Formular (rules) vor onFinish; hier bleibt nur, was keine Feldregel hat.
    const v = form.getFieldsValue(true) as ContractCreateFormValues;
    const startDateIso = formatDateOnlyIso(v.startDate);
    const trainingStartDateIso = formatDateOnlyIso(v.trainingStartDate);
    const closingDateIso = formatDateOnlyIso(v.closingDate);
    if (!v.contractTemplateVariantId || !v.studioId || !startDateIso || !trainingStartDateIso || !closingDateIso) return;
    const optionGroupError = validateOptionGroupSelection(
      selectedVariant?.optionGroups || [],
      contractOptionGroups,
      optionGroupSelection,
      text
    );
    if (optionGroupError) {
      feedback.error(optionGroupError);
      return;
    }

    // Am Sammelkonto bestimmt das Kürzel den Abschlussbenutzer; Abbruch der Abfrage bricht den Abschluss ab.
    let confirmedClosingAccountId = closingAccountId || null;
    if (isCollectionUser) {
      try {
        const acting = await requireAccountAbbreviationConfirmation({ api: auth.api, modal: appModal, actionDescription: text("Add contract package…") });
        confirmedClosingAccountId = acting.id;
      } catch {
        return;
      }
    }

    setSaving(true);
    try {
      const allowOverrides = selectedVariantMeta?.allowOverridesOnCreate !== false;
      const servicesToSend = prepareContractServices(
        (bundleServiceDraft || []).map((service) => ({
          ...service,
          contractTemplateId: String(service.contractTemplateId || service.serviceProductId || ""),
          contractTemplateName: safeDraftText(service.serviceProductName)
        })),
        allowOverrides,
        trainingStartDateIso,
        startDateIso,
        v.servicePaymentMethod
      );

      if (requiresSepaBankDetails && person) {
        await api.put(`/api/v1/persons/${encodeURIComponent(personId)}`, buildPersonBankUpdateDto(person, v));
      }

      await api.post<{ id: string }>("/api/v1/contract-packages", {
        personId,
        studioId: v.studioId,
        contractPackageTemplateId: v.contractTemplateVariantId,
        startDate: startDateIso,
        trainingStartDate: trainingStartDateIso,
        closingDate: closingDateIso,
        paymentMethod: v.paymentMethod != null ? Number(v.paymentMethod) : null,
        paymentPlanId: installmentPlanMeta?.planId || null,
        paymentPlanInstallmentCount: v.paymentPlanInstallmentCount != null ? Number(v.paymentPlanInstallmentCount) : null,
        referrerPersonId: v.referrerPersonId || null,
        memberPromotionTemplateId: v.memberPromotionTemplateId || null,
        services: servicesToSend,
        // Zubuchoptionen: nur die Auswahl; welche Bausteine daraus entstehen und was sie kosten,
        // löst der Server aus der Paketvorlage auf.
        optionGroups: toOptionGroupPayload(optionGroupSelection),
        wallets: prepareContractWallets((bundleWalletDraft || []).map((w) => ({
          ...w,
          walletProductType: w.walletProductType != null ? w.walletProductType : undefined
        }))),
        closingAccountId: confirmedClosingAccountId
      });
      await onCreated();
    } catch {
      // Central API toast shows error.
    } finally {
      setSaving(false);
    }
  };

  useDrawerBaselineEffect(() => { if (open) createContractInitializeDirty(); }, [open]);
  return (
    <FormDrawer dirty={createContractDirty}
      title="Add contract package"
      open={open}
      onCancel={onCancel}
      submitting={saving}
      submitText="Create"
      formId={CONTRACT_CREATE_FORM_ID}
    >
      <Form id={CONTRACT_CREATE_FORM_ID} form={form} layout="vertical" requiredMark={false} autoComplete={INTERNAL_FORM_AUTOCOMPLETE} onValuesChange={onValuesChange} onFinish={() => handleSave()}>
        {/* Das Paket bestimmt alles Weitere (Bausteine, Preise, Zahlungsart) — deshalb zuerst. */}
        <Form.Item
          name="contractTemplateVariantId"
          label={text("Contract package")}
          rules={[{ required: true, message: text("Please select") }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={contractVariantOptions}
            placeholder={text("Select template/variant")}
          />
        </Form.Item>

        <ResponsiveFormGrid>
          <Form.Item name="studioId" label={text("Studio")} rules={[{ required: true, message: text("Studio is required.") }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={allowedStudios.map((s) => ({ value: s.id, label: s.name }))}
              onChange={(sid) => {
                const newId = sid ? String(sid) : "";
                if (!person || !newId) return;
                if (String(person.homeStudioId || "") === newId) return;
                if (!canChangeHomeStudio) return;
                modal.confirm({
                  title: text("Change home studio?"),
                  content: text("Contract package in studio change home").replace("{0}", allowedStudios.find((s) => String(s.id) === newId)?.name ?? text("Studio")),
                  okText: text("Yes, change home studio"),
                  cancelText: text("No"),
                  onOk: async () => {
                    await onChangeHomeStudio(newId);
                  }
                });
              }}
            />
          </Form.Item>
          {isCollectionUser ? null : <Form.Item label={text("Closing user")}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              value={closingAccountId || undefined}
              placeholder={text("— (not specified) —")}
              options={accounts.map((e) => ({
                value: e.id,
                label: accountName(e)
              }))}
              onChange={(v) => setClosingAccountId(v ? String(v) : "")}
              style={{ width: "100%" }}
            />
          </Form.Item>}
        </ResponsiveFormGrid>

        <ResponsiveFormGrid columns={3}>
          <Form.Item
            name="closingDate"
            label={text("Closing date")}
            rules={[{ required: true, message: text("Closing date is required") }]}
          >
            <DatePicker allowClear={false} placeholder={text("Select date")} format={dateInputFormat} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="trainingStartDate"
            label={text("Training start")}
            rules={[{ required: true, message: text("Training start is required") }]}
          >
            <DatePicker allowClear={false} placeholder={text("Select date")} format={dateInputFormat} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="startDate"
            label={text("Contract start")}
            rules={[{ required: true, message: text("Contract start is required") }]}
          >
            <DatePicker allowClear={false} placeholder={text("Select date")} format={dateInputFormat} style={{ width: "100%" }} />
          </Form.Item>
        </ResponsiveFormGrid>

        {canUseMemberPromotions && promotionTemplates.length > 0 ? (
          <>
            <FormSectionHeading title={text("Referral bonus")} sectionKey="person-contract-create.referral-bonus">
              <ResponsiveFormGrid>
                <Form.Item
                  name="referrerPersonId"
                  label={text("Referrer")}
                  dependencies={["memberPromotionTemplateId"]}
                  rules={[{
                    validator: (_, value) => {
                      const templateId = form.getFieldValue("memberPromotionTemplateId");
                      if (!templateId || value) return Promise.resolve();
                      return Promise.reject(new Error(text("Referrer is required.")));
                    }
                  }]}
                >
                  <Select
                    allowClear
                    showSearch
                    filterOption={false}
                    onSearch={searchReferrers}
                    placeholder={text("Search person")}
                    options={referrerOptions.map((referrer) => ({ value: referrer.id, label: personOptionLabel(referrer) }))}
                  />
                </Form.Item>
                <Form.Item
                  name="memberPromotionTemplateId"
                  label={text("Promotion template")}
                  dependencies={["referrerPersonId"]}
                  rules={[{
                    validator: (_, value) => {
                      const referrerPersonId = form.getFieldValue("referrerPersonId");
                      if (!referrerPersonId || value) return Promise.resolve();
                      return Promise.reject(new Error(text("Promotion template is required.")));
                    }
                  }]}
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder={text("Select promotion template")}
                    options={promotionTemplates.map((template) => ({ value: template.id, label: template.name }))}
                  />
                </Form.Item>
              </ResponsiveFormGrid>
            </FormSectionHeading>
          </>
        ) : null}

        <PersonContractCreateContentsSection
          selectedVariant={selectedVariant}
          selectedVariantMeta={selectedVariantMeta}
          bundleServiceDraft={bundleServiceDraft}
          setBundleServiceDraft={setBundleServiceDraft}
          bundleWalletDraft={bundleWalletDraft}
          contractOptionGroups={contractOptionGroups}
          optionGroupSelection={optionGroupSelection}
          setOptionGroupSelection={setOptionGroupSelection}
          contractTemplateDefaultPriceOf={contractTemplateDefaultPriceOf}
          watchedContractStartDate={watchedContractStartDate}
          watchedTrainingStartDate={watchedTrainingStartDate}
          primaryService={primaryService}
          installmentPlanMeta={installmentPlanMeta}
          selectedPaymentPlan={selectedPaymentPlan}
          installmentPreview={installmentPreview}
          walletProducts={walletProducts}
          allowedStudios={allowedStudios}
          paymentMethodSelectOptions={paymentMethodSelectOptions}
          decimalSeparator={decimalSeparator}
          currencyCode={currencyCode}
          moneyPlaceholder={moneyPlaceholder}
          units={units}
          text={text}
          billingCycleLabel={billingCycleLabel}
          cycleUnitLabel={cycleUnitLabel}
          usageLimitUnitLabel={usageLimitUnitLabel}
          timeUnitLabel={timeUnitLabel}
          accessScopeLabel={accessScopeLabel}
        />

        {requiresSepaBankDetails ? (
          <>
            <FormSectionHeading title={text("Bank details")} sectionKey="person-contract-create.bank-details">
              <SepaBankDetailsFields
                api={api}
                dateInputFormat={dateInputFormat}
                defaultMandateSignedDate={watchedClosingDate}
                firstName={person?.firstName}
                form={form}
                homeStudioId={person?.homeStudioId}
                initialIban={person?.iban}
                lastName={person?.lastName}
                modal={modal}
                personId={personId}
                required={false}
                showTitle={false}
                text={text}
              />
            </FormSectionHeading>
          </>
        ) : null}
      </Form>
    </FormDrawer>
  );
}
